/**
 * ColumnPreview — Step 2.
 * Shows detected columns + scrollable sample-data table per sheet.
 * Cells truncate at ~45 chars; full value shown on hover via title tooltip.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { uploadApi, verifyApi } from '../services/api';
import { ColumnPreviewResponse, RowRangesBySheet, SheetColumnInfo } from '../types';
import { ColumnSelector } from '../components/ColumnSelector';
import { NumberHandlingInfo } from '../components/NumberHandlingInfo';
import { runtimeConfig } from '../config/runtimeConfig';

/* ── inline styles so the table behaviour is self-contained ── */
const COL_W = 140;

const S = {
  /** The only element allowed to scroll sideways. */
  tableWrap: {
    overflowX: 'auto' as const,
    overflowY: 'auto' as const,
    maxHeight: 240,
    // Without an explicit width cap this box grows to fit the table and nothing ever
    // overflows — the page scrolls instead. 100% of a parent that is itself allowed to
    // shrink (see .app-main min-width:0) is what makes the inner scrollbar appear.
    width: '100%',
    maxWidth: '100%',
    border: '1px solid var(--border)',
    borderRadius: 6,
  },
  table: {
    borderCollapse: 'collapse' as const,
    // max-content, not 100%: with 48 columns the table must be allowed to be wider than
    // its box so the box can scroll. minWidth keeps a narrow sheet filling the box.
    width: 'max-content' as const,
    minWidth: '100%',
    fontSize: 12,
    tableLayout: 'fixed' as const,     // forces column widths to be respected
  },
  th: (isPhone: boolean) => ({
    background: isPhone ? 'var(--surface-subtle)' : 'var(--surface-muted)',
    color: isPhone ? 'var(--amber)' : 'var(--text-soft)',
    padding: '8px 10px',
    textAlign: 'left' as const,
    fontWeight: 600,
    borderBottom: '1px solid var(--border)',
    whiteSpace: 'nowrap' as const,
    width: COL_W,
    minWidth: COL_W,
    maxWidth: COL_W,
    position: 'sticky' as const,
    top: 0,
    zIndex: 1,
  }),
  td: (isPhone: boolean) => ({
    padding: '6px 10px',
    borderBottom: '1px solid var(--border)',
    color: 'var(--text)',
    background: isPhone ? 'var(--surface-subtle)' : 'transparent',
    width: COL_W,
    minWidth: COL_W,
    maxWidth: COL_W,
    overflow: 'hidden' as const,
    textOverflow: 'ellipsis' as const,
    whiteSpace: 'nowrap' as const,
    cursor: 'default',
  }),
};

function truncate(val: string, max = 45): string {
  return val.length > max ? val.slice(0, max) + '…' : val;
}

interface SampleTableProps {
  sheet: SheetColumnInfo;
  selectedCols: string[];
}

const SampleTable: React.FC<SampleTableProps> = ({ sheet, selectedCols }) => {
  const selectedSet = new Set(selectedCols);
  const rows = sheet.sample_rows ?? [];

  if (rows.length === 0) return null;

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-soft)', marginBottom: 6 }}>
        Sample data — {rows.length} rows &nbsp;
        <span style={{ fontWeight: 400, color: 'var(--text-faint)' }}>
          (📞 = phone column, hover for full value)
        </span>
      </div>

      {/* This box is what scrolls — never the page. */}
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead>
            <tr>
              {sheet.all_columns.map((col) => {
                const isPhone = selectedSet.has(col);
                return (
                  <th key={col} style={S.th(isPhone)} title={col || '(unnamed)'}>
                    {isPhone && <span style={{ marginRight: 4 }}>📞</span>}
                    {truncate(col || '(unnamed)', 18)}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri}>
                {sheet.all_columns.map((col) => {
                  const isPhone = selectedSet.has(col);
                  const raw = row[col] ?? '';
                  return (
                    <td
                      key={col}
                      style={S.td(isPhone)}
                      title={raw}              /* full value on hover */
                    >
                      {truncate(raw, 45)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const emptyRange = (headerRow = 1) => ({ start_row: headerRow + 1, end_row: headerRow + 1 });

function normalizeRanges(
  rowRanges: RowRangesBySheet,
  sheets: SheetColumnInfo[]
): RowRangesBySheet | undefined {
  const out: RowRangesBySheet = {};
  for (const sheet of sheets) {
    const ranges = rowRanges[sheet.sheet_name] || [];
    if (ranges.length === 0) continue;
    out[sheet.sheet_name] = ranges.map((r) => ({
      start_row: Number(r.start_row),
      end_row: Number(r.end_row),
    }));
  }
  return Object.keys(out).length ? out : undefined;
}

function validateRanges(rowRanges: RowRangesBySheet, sheets: SheetColumnInfo[]): string | null {
  for (const sheet of sheets) {
    const ranges = rowRanges[sheet.sheet_name] || [];
    const headerRow = sheet.header_row || 1;
    const firstDataRow = headerRow + 1;
    const maxRow = sheet.row_count + headerRow;
    for (const [idx, range] of ranges.entries()) {
      const label = `${sheet.sheet_name} range ${idx + 1}`;
      if (!Number.isInteger(Number(range.start_row)) || !Number.isInteger(Number(range.end_row))) {
        return `${label} must use whole row numbers.`;
      }
      if (range.start_row <= headerRow) {
        return `${label} must start at row ${firstDataRow} or later. Row ${headerRow} is the header.`;
      }
      if (range.end_row < range.start_row) {
        return `${label} must end at or after its start row.`;
      }
      if (range.end_row > maxRow) {
        return `${label} cannot end after row ${maxRow}.`;
      }
    }
  }
  return null;
}

interface RowRangeSelectorProps {
  sheet: SheetColumnInfo;
  ranges: { start_row: number; end_row: number }[];
  onChange: (ranges: { start_row: number; end_row: number }[]) => void;
}

const RowRangeSelector: React.FC<RowRangeSelectorProps> = ({ sheet, ranges, onChange }) => {
  const maxRow = sheet.row_count + 1;
  const headerRow = sheet.header_row || 1;
  const firstDataRow = headerRow + 1;
  const usingAllRows = ranges.length === 0;
  const selectedRows = usingAllRows
    ? sheet.row_count
    : ranges.reduce((sum, r) => sum + Math.max(0, Number(r.end_row) - Number(r.start_row) + 1), 0);

  const updateRange = (idx: number, field: 'start_row' | 'end_row', value: number) => {
    onChange(ranges.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };

  return (
    <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 14, marginTop: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <div>
          <div style={{ color: 'var(--text-strong)', fontWeight: 700, fontSize: 13 }}>Rows to dial</div>
          <div style={{ color: 'var(--text-faint)', fontSize: 12 }}>
            Header is row {headerRow}. Data rows are {firstDataRow}-{maxRow}. Selected rows: {selectedRows}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" className="btn btn-secondary" onClick={() => onChange([])}>
            All Rows
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => onChange(ranges.length ? [...ranges, emptyRange(headerRow)] : [{ start_row: firstDataRow, end_row: maxRow }])}
          >
            Add Range
          </button>
        </div>
      </div>

      {!usingAllRows && (
        <div style={{ display: 'grid', gap: 10, marginTop: 12 }}>
          {ranges.map((range, idx) => (
            <div
              key={idx}
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(90px, 1fr) minmax(90px, 1fr) auto',
                gap: 8,
                alignItems: 'end',
              }}
            >
              <label style={{ fontSize: 12, color: 'var(--text-soft)' }}>
                Start row
                <input
                  type="number"
                  min={firstDataRow}
                  max={maxRow}
                  value={range.start_row}
                  onChange={(e) => updateRange(idx, 'start_row', Number(e.target.value))}
                  style={{ width: '100%', marginTop: 4 }}
                />
              </label>
              <label style={{ fontSize: 12, color: 'var(--text-soft)' }}>
                End row
                <input
                  type="number"
                  min={firstDataRow}
                  max={maxRow}
                  value={range.end_row}
                  onChange={(e) => updateRange(idx, 'end_row', Number(e.target.value))}
                  style={{ width: '100%', marginTop: 4 }}
                />
              </label>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => onChange(ranges.filter((_, i) => i !== idx))}
                aria-label={`Remove range ${idx + 1}`}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ── Main page ── */

const ColumnPreview: React.FC = () => {
  const navigate = useNavigate();
  const { uploadData, selectedSheets, setConfirmedColumns, setCurrentJobId } = useApp();

  const [preview, setPreview] = useState<ColumnPreviewResponse | null>(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const [overage, setOverage] = useState(false);  // 402 — out of dial credits
  const [columnSelections, setColumnSelections] = useState<Record<string, string[]>>({});
  const [rowRanges, setRowRanges] = useState<RowRangesBySheet>({});
  const [headerRows, setHeaderRows] = useState<Record<string, number>>({});

  useEffect(() => {
    if (!uploadData || selectedSheets.length === 0) { navigate('/upload'); return; }
    (async () => {
      try {
        const data = await uploadApi.previewColumns(uploadData.upload_id, selectedSheets);
        setPreview(data);
        const initial: Record<string, string[]> = {};
        for (const s of data.sheets) initial[s.sheet_name] = s.detected_columns.map((c) => c.name);
        setColumnSelections(initial);
        setHeaderRows(Object.fromEntries(data.sheets.map((s) => [s.sheet_name, s.header_row || 1])));
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Failed to detect columns');
      } finally {
        setLoading(false);
      }
    })();
  }, []); // eslint-disable-line

  const totalSelected = Object.values(columnSelections).flat().length;

  const handleSheetColumnChange = useCallback((sheetName: string, selected: string[]) => {
    setColumnSelections((prev) => ({ ...prev, [sheetName]: selected }));
  }, []);

  const reloadForHeaderRows = async (nextHeaderRows: Record<string, number>) => {
    if (!uploadData) return;
    setLoading(true);
    setError(null);
    try {
      const data = await uploadApi.previewColumns(uploadData.upload_id, selectedSheets, nextHeaderRows);
      setPreview(data);
      setHeaderRows(Object.fromEntries(data.sheets.map((s) => [s.sheet_name, s.header_row || 1])));
      setColumnSelections((prev) => {
        const next: Record<string, string[]> = {};
        for (const s of data.sheets) {
          const existing = prev[s.sheet_name] || [];
          next[s.sheet_name] = existing.filter((c) => s.all_columns.includes(c));
          if (next[s.sheet_name].length === 0) {
            next[s.sheet_name] = s.detected_columns.map((c) => c.name);
          }
        }
        return next;
      });
      setRowRanges({});
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to reload columns for that header row');
    } finally {
      setLoading(false);
    }
  };

  const handleRangesChange = useCallback((sheetName: string, ranges: { start_row: number; end_row: number }[]) => {
    setRowRanges((prev) => ({ ...prev, [sheetName]: ranges }));
  }, []);

  const applyFirstRangeToAll = () => {
    const firstSheet = preview?.sheets[0];
    if (!firstSheet) return;
    const firstRanges = rowRanges[firstSheet.sheet_name] || [];
    setRowRanges((prev) => {
      const next: RowRangesBySheet = { ...prev };
      for (const sheet of preview?.sheets || []) {
        next[sheet.sheet_name] = firstRanges.map((r) => ({ ...r }));
      }
      return next;
    });
  };

  const handleNext = async () => {
    if (!uploadData) return;
    if (!runtimeConfig.concurrentCalls) {
      setError(
        runtimeConfig.concurrentCallsError ||
          'Missing REACT_APP_CONCURRENT_CALLS in frontend/.env.local'
      );
      return;
    }
    setIsStarting(true);
    setError(null);
    try {
      const validationError = validateRanges(rowRanges, preview?.sheets || []);
      if (validationError) {
        setError(validationError);
        setIsStarting(false);
        return;
      }
      const normalizedRowRanges = normalizeRanges(rowRanges, preview?.sheets || []);
      await uploadApi.confirmColumns(uploadData.upload_id, selectedSheets, columnSelections, normalizedRowRanges, headerRows);
      setConfirmedColumns(columnSelections);
      const res = await verifyApi.startVerification({
        upload_id: uploadData.upload_id,
        selected_sheets: selectedSheets,
        column_map: columnSelections,
        row_ranges: normalizedRowRanges,
        header_rows: headerRows,
        concurrent_calls: runtimeConfig.concurrentCalls,
      });
      setCurrentJobId(res.job_id);
      navigate('/history');
    } catch (err: any) {
      setOverage(err.response?.status === 402);  // no credits / no active plan
      setError(err.response?.data?.detail || 'Failed to start verification');
      setIsStarting(false);
    }
  };

  if (loading) return (
    <div style={{ textAlign: 'center', padding: 60 }}>
      <div className="spinner" />
      <p className="text-muted mt-3">Detecting phone columns…</p>
    </div>
  );

  return (
    // maxWidth:100% + minWidth:0 so a wide sheet cannot stretch this page past the
    // viewport; the sample table scrolls inside its own box instead.
    <div style={{ maxWidth: '100%', minWidth: 0 }}>
      <h2 style={{ fontSize: 20, marginBottom: 4, color: 'var(--text-strong)' }}>Confirm Phone Columns</h2>
      <p style={{ marginBottom: 20, color: 'var(--text-soft)' }}>
        Review detected columns and sample data. Uncheck anything you don't want to dial.
      </p>

      {error && (
        <div className="alert alert-error">
          ❌ {error}
          {overage && (
            <>
              {' '}
              <Link to="/subscription" style={{ color: 'var(--amber)', fontWeight: 600, textDecoration: 'underline' }}>
                Redeem a code →
              </Link>
            </>
          )}
        </div>
      )}
      {preview?.explanation && <div className="alert alert-success">✅ {preview.explanation}</div>}
      {preview?.gemini_used && (
        <div className="alert alert-purple">
          🤖 Gemini AI identified phone columns (pattern matching found none).
        </div>
      )}

      <NumberHandlingInfo />

      {preview?.sheets.map((sheet: SheetColumnInfo) => (
        <div key={sheet.sheet_name} className="card" style={{ marginBottom: 20 }}>
          {/* Sheet header */}
          <div className="card-header">
            <span className="card-title">📋 {sheet.sheet_name}</span>
            <span className="text-muted" style={{ fontSize: 12 }}>{sheet.row_count} rows</span>
          </div>

          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 12, flexWrap: 'wrap', marginBottom: 14,
            padding: '10px 12px', border: '1px solid var(--border-subtle)',
            borderRadius: 6, background: 'var(--surface-subtle)',
          }}>
            <div>
              <div style={{ color: 'var(--text-strong)', fontWeight: 700, fontSize: 13 }}>
                Header row
              </div>
              <div style={{ color: 'var(--text-faint)', fontSize: 12 }}>
                Auto-detected with {sheet.header_confidence} confidence. Confirm this row before selecting ranges.
              </div>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-soft)', fontSize: 12 }}>
              Row
              <input
                type="number"
                min={1}
                value={headerRows[sheet.sheet_name] ?? sheet.header_row ?? 1}
                onChange={(e) => {
                  const value = Math.max(1, Number(e.target.value || 1));
                  setHeaderRows((prev) => ({ ...prev, [sheet.sheet_name]: value }));
                }}
                onBlur={() => reloadForHeaderRows(headerRows)}
                style={{ width: 80 }}
              />
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => reloadForHeaderRows(headerRows)}
              >
                Apply
              </button>
            </label>
          </div>

          {/* Scrollable horizontal sample table */}
          <SampleTable
            sheet={sheet}
            selectedCols={columnSelections[sheet.sheet_name] || []}
          />

          {/* Column check/uncheck selector */}
          <ColumnSelector
            sheetName={sheet.sheet_name}
            allColumns={sheet.all_columns}
            detectedColumns={sheet.detected_columns}
            selected={columnSelections[sheet.sheet_name] || []}
            onChange={(sel) => handleSheetColumnChange(sheet.sheet_name, sel)}
            dialableCounts={sheet.dialable_counts}
          />

          <RowRangeSelector
            sheet={sheet}
            ranges={rowRanges[sheet.sheet_name] || []}
            onChange={(ranges) => handleRangesChange(sheet.sheet_name, ranges)}
          />
        </div>
      ))}

      {/* Footer nav */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 24 }}>
        <button className="btn btn-secondary" onClick={() => navigate('/upload')}>← Back</button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
          <button type="button" className="btn btn-secondary" onClick={applyFirstRangeToAll}>
            Apply First Range To All
          </button>
          <span className="text-muted" style={{ fontSize: 13 }}>
            {totalSelected} column{totalSelected !== 1 ? 's' : ''} selected
          </span>
        </div>
        <button
          className="btn btn-primary"
          onClick={handleNext}
          disabled={totalSelected === 0 || isStarting}
        >
          {isStarting ? 'Starting…' : 'Start Run →'}
        </button>
      </div>
    </div>
  );
};

export default ColumnPreview;
